// THE CHARACTER — one authored, skinned mesh, worn by everybody.
//
// What this replaces is render/humanoid.ts: forty lofted solids merged by
// material into twenty-four meshes and nine materials per figure, rebuilt from
// scratch for every appearance in the room. What stands here instead is a
// single baked model (tools/mesh → apps/web/public/assets/character), loaded
// once, and shared by the player, every remote avatar, the creator's preview
// and the street crowd.
//
// THREE THINGS MAKE ONE MESH SERVE EVERY APPEARANCE:
//
// 1. ZONES. Every vertex carries a material id in `_detail.x` — skin, hair,
//    accent, base, leather, brass, seam, lens — and the shader looks the
//    colour up in `uZoneTint[8]`. So a player's palette is eight vec3s of
//    uniform rather than eight textures, and two avatars in different clothes
//    share geometry, atlas, and every byte of VRAM.
//
// 2. PIECES. The catalogue's indices pick which baked meshes to show: one
//    body, one of six garment shells, a sleeve shell or none, one of seven
//    heads of hair, an accessory. Adding a hairstyle is a new mesh in the
//    GLB, never a new body and never a new texture.
//
// 3. BAKED PER-VERTEX AO AND WEAR ride in `_detail.yz`. The AO is ray-traced
//    offline against the pieces that are worn together, so an armpit, an eye
//    socket, the web between two fingers and the inside of a lapel are dark
//    without a screen-space pass — which is the pass a browser on integrated
//    graphics cannot buy. The wear scalar is what turns the four catalogue
//    tiers into four visibly different garments: fresh-pressed multiplies it
//    by zero, aether-scorched by one, and the dust, the bleach and the char
//    land where a life would have put them.
//
// THE CROWD IS THE SAME MESH. `crowdBatch()` bakes the walk cycle into a
// texture of bone matrices and instances the LOD2 pieces through a patched
// skinning shader, so ninety-six pedestrians are three draw calls of the same
// character the player is wearing — not a second, worse humanoid written in
// render/city.ts.

import {
  ACCESSORIES,
  type Appearance,
  BUILDS,
  HAIR_COLORS,
  HEIGHT_MAX,
  HEIGHT_MIN,
  OUTFIT_COLORS,
  OUTFITS,
  PLAYER_HEIGHT,
  SKIN_TONES,
  WEAR_LEVELS,
} from "@bebop/shared";
import * as THREE from "three";
import { KTX2Loader } from "three/addons/loaders/KTX2Loader.js";
import { manifest } from "./assets";
import { contactShadow } from "./attire";
import { getAuthoredTemplate, preloadAuthoredAssets } from "./authored-assets";
import { faceDecal } from "./face";

// ---------------------------------------------------------------------------
// The loaded kit.

interface Kit {
  geometries: Map<string, THREE.BufferGeometry>;
  /** The skeleton the GLB was authored against; every avatar clones it. */
  template: THREE.Skeleton;
  rootBone: THREE.Bone;
  albedo: THREE.Texture;
  normal: THREE.Texture;
  orm: THREE.Texture;
  /** Bone name → index, so the animation driver never counts nodes. */
  index: Map<string, number>;
}

let kit: Kit | undefined;
let bytes = 0;

export const characterBytes = (): number => bytes;
export const characterReady = (): boolean => kit !== undefined;

function dress(tex: THREE.Texture, srgb: boolean, maxAniso: number): THREE.Texture {
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  // The atlas is a UNIQUE unwrap: nothing tiles, so clamping is both correct
  // and one less way for a UV a hair outside an island to wrap to the far
  // side of the sheet.
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  // 16, not 8 — the ground crust's number, for the ground crust's reason.
  // A limb is a cylinder: its silhouette edges are ALWAYS at grazing
  // incidence, and unlike a wall they are grazing in a direction that
  // changes every frame as the figure turns. This is the map class with the
  // strongest case for anisotropy in the game and it was running at half
  // what the paving gets. (The mip chain itself was never missing: every
  // character map ships twelve levels — see tools/assets/src/ktx2.ts.)
  tex.anisotropy = Math.min(16, maxAniso);
  tex.flipY = false;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Load the character. Called from the boot gate, before anything renders —
 * the same rule the material set follows and for the same reason: an avatar
 * that pops in two seconds after the world does is a worse first frame than
 * a load screen that lasts two seconds longer.
 */
export async function loadCharacter(renderer?: THREE.WebGLRenderer): Promise<void> {
  if (kit) return;
  const m = manifest();
  const entry = m?.character;
  if (!entry) throw new Error("manifest has no character — run `pnpm assets:build`");
  const probe = renderer ?? new THREE.WebGLRenderer();
  const maxAniso = probe.capabilities.getMaxAnisotropy();
  const ktx2 = new KTX2Loader().detectSupport(probe);
  ktx2.setWorkerLimit(2);
  try {
    const base = "/assets/";
    const [, albedo, normal, orm] = await Promise.all([
      preloadAuthoredAssets(probe, ["character"]),
      ktx2.loadAsync(base + entry.atlas.albedo.url),
      ktx2.loadAsync(base + entry.atlas.normal.url),
      ktx2.loadAsync(base + entry.atlas.orm.url),
    ]);
    // Authored geometry is counted by authoredBytes; these are the shared
    // customization atlas bytes only, avoiding counting the GLB twice.
    bytes = entry.atlas.albedo.bytes + entry.atlas.normal.bytes + entry.atlas.orm.bytes;
    const scene = getAuthoredTemplate("character");
    if (!scene) throw new Error("Authored character is missing after preload");

    const geometries = new Map<string, THREE.BufferGeometry>();
    let template: THREE.Skeleton | undefined;
    scene.traverse((obj) => {
      const mesh = obj as THREE.SkinnedMesh;
      if (!mesh.isSkinnedMesh) return;
      const geo = mesh.geometry;
      // GLTFLoader names a custom attribute by lower-casing it; the shader
      // wants a name it can write without underscores.
      const detail = geo.getAttribute("_detail");
      if (detail) {
        geo.setAttribute("detail", detail);
        geo.deleteAttribute("_detail");
      }
      if (!geo.getAttribute("detail") || !geo.getAttribute("skinWeight")) {
        throw new Error(`Character piece ${mesh.name} lost its detail or skin attributes`);
      }
      geometries.set(mesh.name, geo);
      template ??= mesh.skeleton;
    });
    if (!template) throw new Error("character GLB has no skinned mesh");
    let rootBone: THREE.Bone | undefined;
    scene.traverse((obj) => {
      const bone = obj as THREE.Bone;
      if (bone.isBone && !(bone.parent as THREE.Bone | null)?.isBone) rootBone ??= bone;
    });
    if (!rootBone) throw new Error("character GLB has no skeleton root");

    kit = {
      geometries,
      template,
      rootBone,
      albedo: dress(albedo, true, maxAniso),
      normal: dress(normal, false, maxAniso),
      orm: dress(orm, false, maxAniso),
      index: new Map(template.bones.map((b, i) => [b.name, i])),
    };
  } finally {
    ktx2.dispose();
    if (!renderer) probe.dispose();
  }
}

// ---------------------------------------------------------------------------
// The palette. Every value below is exactly what six rounds of grading landed
// on in render/humanoid.ts; what changed is that they are uniforms now.

const BASE_DARK = 0x23232e;
const BASE_PANTS = 0x2c2c38;
const BOOT = 0x191920;
const BRASS = 0xb08d3c;
const SEAM = 0x1a150f;
const LENS = 0x0d1420;
/** What washed-out cloth washes toward. */
const WEAR_GRAY = new THREE.Color(0x5a564f);
/** What the road leaves on a hem: pale ochre grit, never a dark slab. */
const ROAD_DUST = new THREE.Color(0x7d7160);
/** What the Edge leaves: char, with a cold residue ringing it. */
const SCORCH_DUST = new THREE.Color(0x4c4740);

/**
 * THE FIGURE'S RESPONSE SET, in ZONE order (see tools/mesh/src/atlas.ts):
 * skin, hair, accent, base cloth, leather, brass, seam, lens.
 *
 * The judges' sentence about the avatar was "carries zero specular anywhere",
 * and the table it was filed against is worth keeping in the diff:
 *
 *     rough  0.72  0.86  0.82  0.94  0.52  0.24  1.00  0.08
 *     metal  0.00  0.00  0.04  0.00  0.18  0.90  0.00  0.62
 *
 * Two things were wrong with it and only one of them is the roughness.
 *
 * SKIN AT 0.72 IS CHALK. Skin is one of the glossiest things a person is
 * wearing — a broad, low-intensity specular off the sebum layer, which is why
 * a face has a highlight down the nose and along the cheekbone and a plaster
 * wall does not. 0.48 is that lobe.
 *
 * AND THREE OF THE EIGHT ZONES WERE HALF METAL. Accent cloth at 0.04, leather
 * at 0.18 and the goggle lens at 0.62 are all dielectrics; metalness on a
 * dielectric deletes a fraction of its diffuse and tints a fraction of its
 * specular by its own albedo, which is exactly the uncanny middle the material
 * pass exists to remove. Brass goes the other way, to a full 1.
 *
 * The two columns that did not exist are the ones that make a figure read as
 * FOUR materials rather than one: normal-incidence reflectance, so leather and
 * a goggle lens do not share plaster's 0.04, and an indirect-specular gain, so
 * the brass fittings return the sky at six times what the coat does.
 */
const ZONE_ROUGH = [0.48, 0.62, 0.88, 0.92, 0.42, 0.16, 0.96, 0.06];
const ZONE_METAL = [0.0, 0.0, 0.0, 0.0, 0.0, 1.0, 0.0, 0.0];
/** Dielectric F0. Skin 0.028, oiled leather 0.055, goggle glass 0.09. */
const ZONE_SPEC = [0.028, 0.05, 0.026, 0.026, 0.055, 0.04, 0.02, 0.09];
/** Indirect-specular gain — the probe, per zone. Never touches irradiance. */
const ZONE_ENV = [1.2, 1.4, 1.0, 1.0, 2.2, 6.0, 1.0, 8.0];
/** Millimetre fabric and skin detail must not overwhelm modeled forms. */
const ZONE_NORMAL = [0.2, 0.38, 0.34, 0.34, 0.58, 0.38, 0.35, 0.02];

/** The head island, and the slice of it the drawn face covers. */
const HEAD_RECT = new THREE.Vector4(0.0, 0.0, 0.5, 0.25);
/** Half-width in island-u, top in island-v, height in island-v. */
const FACE_SPAN = new THREE.Vector3(0.17, 0.24, 0.52);

export interface CharacterOptions {
  /** 0 = the player's own body, 2 = a pedestrian across the street. */
  lod?: 0 | 1 | 2;
  /** Skip the ground-contact quads (the creator's pedestal has its own). */
  contact?: boolean;
}

export interface Character {
  group: THREE.Group;
  /** Advance the walk cycle; speed in m/s, dt in seconds. */
  animate(speed: number, dt: number): void;
  /** Sole to crown, metres. */
  height: number;
  dispose(): void;
}

function zoneTints(a: Appearance): THREE.Color[] {
  const wear = WEAR_LEVELS[a.wear] ?? WEAR_LEVELS[0];
  const garb = OUTFITS[a.outfit] ?? OUTFITS[0];
  /**
   * Cloth responds to wear; skin and hair do not. The curve is amplified
   * (tint^1.6, gray×1.6) so the tiers read at camera distance — at the
   * fresh-pressed index both terms are identity, preserving the v1 look.
   */
  const cloth = (hex: number): THREE.Color =>
    new THREE.Color(hex)
      .multiplyScalar((wear?.tint ?? 1) ** 1.6)
      .lerp(WEAR_GRAY, Math.min(1, (wear?.gray ?? 0) * 1.6));
  const accent = OUTFIT_COLORS[a.outfitColor] ?? OUTFIT_COLORS[0] ?? 0x8f2d3c;
  const base = garb?.id === "duster" ? BASE_DARK : BASE_PANTS;
  return [
    new THREE.Color(SKIN_TONES[a.skinTone] ?? SKIN_TONES[0] ?? 0xd9a066),
    new THREE.Color(HAIR_COLORS[a.hairColor] ?? HAIR_COLORS[0] ?? 0x1b1b22),
    cloth(accent),
    cloth(base),
    cloth(BOOT).lerp(new THREE.Color(BOOT), 0.5),
    new THREE.Color(BRASS),
    new THREE.Color(SEAM),
    new THREE.Color(LENS),
  ];
}

/**
 * The character shader.
 *
 * A `MeshStandardMaterial` with four injections, because everything a
 * character needs that a glTF material cannot say is per-ZONE or per-TIER:
 *
 *   the zone tint, which is what one mesh dressing every appearance means;
 *   the wear terms, which bleach, dust and char the baked wear mask;
 *   the baked AO, which rides a vertex attribute rather than a second UV set;
 *   the face decal, which is the one thing still generated at runtime because
 *   it is the one thing that is genuinely per player — eye shape and spacing,
 *   brow weight, and the marks of a life lived.
 */
function characterMaterial(
  a: Appearance,
  face: THREE.Texture | undefined,
): THREE.MeshStandardMaterial {
  if (!kit) throw new Error("character not loaded");
  const wear = WEAR_LEVELS[a.wear] ?? WEAR_LEVELS[0];
  const tier = a.wear;
  const scorched = wear?.id === "scorched";
  const mat = new THREE.MeshStandardMaterial({
    map: kit.albedo,
    normalMap: kit.normal,
    roughnessMap: kit.orm,
    metalnessMap: kit.orm,
    roughness: 1,
    metalness: 1,
    normalScale: new THREE.Vector2(0.85, 0.85),
    envMapIntensity: 1.2,
  });
  const tints = zoneTints(a);
  const uniforms = {
    uZoneTint: { value: tints },
    uZoneRough: { value: ZONE_ROUGH },
    uZoneMetal: { value: ZONE_METAL },
    uZoneSpec: { value: ZONE_SPEC },
    uZoneEnv: { value: ZONE_ENV },
    uZoneNormal: { value: ZONE_NORMAL },
    // fade (sun bleach), dust, char, and how much of the AO to spend.
    uWear: {
      value: new THREE.Vector4(
        [0, 0.26, 0.46, 0.4][tier] ?? 0,
        [0, 0.16, 0.5, 0.42][tier] ?? 0,
        scorched ? 0.72 : 0,
        0.9,
      ),
    },
    uDust: { value: scorched ? SCORCH_DUST : ROAD_DUST },
    uFace: { value: face ?? null },
    uFaceOn: { value: face ? 1 : 0 },
    uHeadRect: { value: HEAD_RECT },
    uFaceSpan: { value: FACE_SPAN },
  };
  mat.userData.uniforms = uniforms;
  mat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    if (mat.userData.instanced) {
      /**
       * THE CROWD'S SKINNING PATH.
       *
       * three's own chunk reads a bone matrix out of the SkinnedMesh's bone
       * texture; this one reads it out of a baked cycle, at the row a
       * per-instance attribute names. Everything downstream — skinbase_vertex,
       * skinning_vertex, skinnormal_vertex — is three's, unchanged, so the
       * deformation is the deformation, not an approximation of it.
       *
       * bindMatrix is a compile-time identity because the mesh node the GLB
       * authored has no transform; making it a constant means the renderer
       * never has to be told about a uniform it only sets for real
       * SkinnedMeshes.
       */
      shader.vertexShader = shader.vertexShader.replace(
        "#include <skinning_pars_vertex>",
        `uniform highp sampler2D boneTexture;
attribute float aFrame;
const mat4 bindMatrix = mat4(1.0);
const mat4 bindMatrixInverse = mat4(1.0);
mat4 getBoneMatrix(const in float i) {
  int j = int(i) * 4;
  int row = int(aFrame + 0.5);
  return mat4(
    texelFetch(boneTexture, ivec2(j, row), 0),
    texelFetch(boneTexture, ivec2(j + 1, row), 0),
    texelFetch(boneTexture, ivec2(j + 2, row), 0),
    texelFetch(boneTexture, ivec2(j + 3, row), 0)
  );
}`,
      );
    }
    shader.vertexShader = shader.vertexShader
      .replace(
        "#include <common>",
        `#include <common>
attribute vec4 detail;
varying vec3 vDetail;`,
      )
      .replace(
        "#include <begin_vertex>",
        `#include <begin_vertex>
vDetail = vec3(detail.x, detail.y / 255.0, detail.z / 255.0);`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        `#include <common>
uniform vec3 uZoneTint[8];
uniform float uZoneRough[8];
uniform float uZoneMetal[8];
uniform float uZoneSpec[8];
uniform float uZoneEnv[8];
uniform float uZoneNormal[8];
uniform vec4 uWear;
uniform vec3 uDust;
uniform sampler2D uFace;
uniform float uFaceOn;
uniform vec4 uHeadRect;
uniform vec3 uFaceSpan;
varying vec3 vDetail;`,
      )
      .replace(
        "#include <color_fragment>",
        `#include <color_fragment>
{
  int zone = int(vDetail.x + 0.5);
  // The shared atlas is deliberately neutral. Restrain its high-frequency
  // colour variation on skin so cheeks read as anatomy rather than plaster.
  if (zone == 0) diffuseColor.rgb = mix(vec3(0.50), diffuseColor.rgb, 0.42);
  diffuseColor.rgb *= uZoneTint[zone];
  // Cloth wears; skin, hair, brass and glass do not.
  float clothy = (zone == 2 || zone == 3 || zone == 4 || zone == 6) ? 1.0 : 0.0;
  float w = vDetail.z * clothy;
  // Sun bleaches toward a pale grey-ochre; the road dusts; the Edge chars.
  diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * 1.55 + vec3(0.06), uWear.x * w);
  diffuseColor.rgb = mix(diffuseColor.rgb, uDust, uWear.y * w * 0.75);
  diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * 0.28 + vec3(0.012, 0.014, 0.02), uWear.z * w);
  // The drawn face: eyes, brows, mouth and marks, on the head island only.
  if (uFaceOn > 0.5 && vMapUv.x >= uHeadRect.x && vMapUv.x < uHeadRect.x + uHeadRect.z
      && vMapUv.y >= uHeadRect.y && vMapUv.y < uHeadRect.y + uHeadRect.w) {
    vec2 hs = (vMapUv - uHeadRect.xy) / uHeadRect.zw;
    // The head unwraps with its seam at the FACE midline, so the decal's own
    // coordinate has to be taken through the wrap: fract(s + 0.5) − 0.5 puts
    // zero on the nose and the discontinuity at the back of the skull, where
    // the decal is transparent and nobody can see a derivative go wrong.
    float fs = fract(hs.x + 0.5) - 0.5;
    vec2 fuv = vec2(fs / (uFaceSpan.x * 2.0) + 0.5, (hs.y - uFaceSpan.y) / uFaceSpan.z);
    if (fuv.x > 0.0 && fuv.x < 1.0 && fuv.y > 0.0 && fuv.y < 1.0) {
      // EXPLICIT GRADIENTS, because fract() is discontinuous at the seam and
      // an automatic derivative there jumps to the coarsest mip — which draws
      // a soft dark line straight down the middle of the face, in the one
      // shot the creator exists to sell. vMapUv is continuous inside every
      // triangle, so its derivative is the honest one.
      vec2 scale = 1.0 / (uHeadRect.zw * vec2(uFaceSpan.x * 2.0, uFaceSpan.z));
      vec4 ink = textureGrad(uFace, fuv, dFdx(vMapUv) * scale, dFdy(vMapUv) * scale);
      diffuseColor.rgb = mix(diffuseColor.rgb, ink.rgb, ink.a);
    }
  }
}`,
      )
      .replace(
        "#include <normal_fragment_maps>",
        THREE.ShaderChunk.normal_fragment_maps.replace(
          "mapN.xy *= normalScale;",
          "mapN.xy *= normalScale * uZoneNormal[int(vDetail.x + 0.5)];",
        ),
      )
      .replace(
        "#include <roughnessmap_fragment>",
        `#include <roughnessmap_fragment>
{
  int zone = int(vDetail.x + 0.5);
  // The atlas writes roughness around 0.5 = "this material's own"; the zone
  // says what that is, and wear only ever adds.
  roughnessFactor = clamp(uZoneRough[zone] + (texelRoughness.g - 0.5) * 0.7 + uWear.x * 0.12, 0.04, 1.0);
}`,
      )
      .replace(
        "#include <metalnessmap_fragment>",
        `#include <metalnessmap_fragment>
{
  int zone = int(vDetail.x + 0.5);
  metalnessFactor = uZoneMetal[zone] * clamp(texelMetalness.b * 2.0, 0.0, 1.0);
}`,
      )
      // THE TWO TERMS A ROUGHNESS MAP CANNOT CARRY, per zone, exactly as
      // render/forge.ts does them for the world's surfaces. F0 first: three
      // hardcodes 0.04 for every MeshStandardMaterial, which is right for
      // nothing on a person — skin scatters at 0.028 and an oiled boot at
      // 0.055, and that difference is most of why one reads as skin.
      .replace(
        "#include <lights_physical_fragment>",
        `#include <lights_physical_fragment>
{
  int zone = int(vDetail.x + 0.5);
  material.specularColor = vec3(uZoneSpec[zone]);
  material.specularColorBlended = mix(material.specularColor, diffuseColor.rgb, metalnessFactor);

  // GEOMETRIC SPECULAR ANTIALIASING — WAVE 14 REGRESSION FIX.
  //
  // The wave that gave the figure a real response set took skin from 0.72
  // to 0.48, hair to 0.62, brass to 0.16 and the goggle lens to 0.06, and
  // put a probe with an unclipped HDR sun disc behind all four. Those are
  // the right numbers and the frames got measurably worse: the judges
  // logged second-derivative luma energy up 1.6–2.8x ON CHARACTERS ONLY
  // while the environment stayed flat, and read it as missing mips. It is
  // not a mip problem — every character map ships a full chain (12 levels
  // at 2048) — it is SPECULAR ALIASING, and it is character-specific for
  // the obvious reason: a person is the only thing in frame whose shading
  // normal turns through 180 degrees inside forty screen pixels. A narrow
  // lobe on a normal that swings a radian per pixel cannot be point
  // sampled, and no amount of albedo filtering touches it.
  //
  // The fix is the standard one (Kaplanyan et al., "Filtering
  // distributions of normals"): estimate the per-pixel variance of the
  // shading normal from its screen-space derivatives and widen the lobe by
  // it, in variance space. At arm's length the derivatives are tiny and
  // this is identity — the creator's close-ups keep every bit of the
  // response set. Across the street it converges the lobe toward the
  // pixel's own normal cone, which is what a correctly prefiltered BRDF
  // would have done anyway.
  vec3 dNx = dFdx( normal );
  vec3 dNy = dFdy( normal );
  float variance = 0.5 * ( dot( dNx, dNx ) + dot( dNy, dNy ) );
  float widen = min( 2.0 * variance, 0.18 );
  material.roughness = min( 1.0, sqrt( material.roughness * material.roughness + widen ) );
}`,
      )
      // …and the probe, scaled per zone on the SPECULAR half only. The brass
      // fittings return the sky at six times what the coat does; the coat's
      // ambient fill does not move, because `radiance` here holds nothing but
      // the image-based specular.
      .replace(
        "#include <lights_fragment_maps>",
        `#include <lights_fragment_maps>
{
  int zone = int(vDetail.x + 0.5);
  radiance *= uZoneEnv[zone];
}`,
      )
      .replace(
        "#include <aomap_fragment>",
        `#include <aomap_fragment>
// Ray-traced offline against the pieces this one is worn with. It is the
// armpit, the eye socket, the web between two fingers and the inside of a
// lapel, on a renderer that cannot afford to find them per frame.
reflectedLight.indirectDiffuse *= mix(1.0, vDetail.y, uWear.w);`,
      );
  };
  // Two materials whose onBeforeCompile bodies differ must not share a
  // program; the key is what three hashes them by.
  mat.customProgramCacheKey = () =>
    `bebop-character-${face ? "face" : "plain"}-${mat.userData.instanced ? "crowd" : "solo"}`;
  return mat;
}

// ---------------------------------------------------------------------------
// One avatar.

function cloneSkeleton(): { root: THREE.Bone; skeleton: THREE.Skeleton } {
  if (!kit) throw new Error("character not loaded");
  const root = kit.rootBone.clone(true) as THREE.Bone;
  const byName = new Map<string, THREE.Bone>();
  root.traverse((o) => {
    const bone = o as THREE.Bone;
    if (bone.isBone) byName.set(bone.name, bone);
  });
  const bones = kit.template.bones.map((b) => byName.get(b.name) ?? new THREE.Bone());
  return {
    root,
    skeleton: new THREE.Skeleton(
      bones,
      kit.template.boneInverses.map((m) => m.clone()),
    ),
  };
}

/** Which baked meshes an appearance wears, at a given level of detail. */
export function piecesFor(a: Appearance, lod: 0 | 1 | 2): string[] {
  const wear = WEAR_LEVELS[a.wear] ?? WEAR_LEVELS[0];
  const garb = OUTFITS[a.outfit] ?? OUTFITS[0];
  const acc = ACCESSORIES[a.accessory] ?? ACCESSORIES[0];
  const suffix = `_lod${lod}`;
  const small = `_lod${Math.min(lod, 1)}`;
  const out = [`body${suffix}`, `garb${a.outfit}${suffix}`];
  // The road rolls a sleeve, and it is the loudest tier read there is: a
  // forearm of bare skin where cloth used to be, legible across a street.
  const sleeve = garb?.id === "deckhand" ? 0 : garb?.id === "machinist" ? 0.45 : 1;
  if (sleeve > 0.9) out.push(`${a.wear >= 2 ? "sleeveRolled" : "sleeveFull"}${suffix}`);
  else if (sleeve > 0.2) out.push(`sleeveRolled${suffix}`);
  if (wear?.patches && lod < 2) out.push(`patches${small}`);
  if (lod < 2 || a.hair !== 0) out.push(`hair${a.hair}${small}`);
  if (acc && acc.id !== "none" && lod < 2) out.push(`acc${a.accessory}${small}`);
  return out;
}

export function createCharacter(a: Appearance, opts: CharacterOptions = {}): Character {
  if (!kit) throw new Error("character not loaded");
  const lod = opts.lod ?? 0;
  const group = new THREE.Group();
  const { root, skeleton } = cloneSkeleton();
  group.add(root);

  const heightScale = HEIGHT_MIN + (HEIGHT_MAX - HEIGHT_MIN) * a.height;
  const height = PLAYER_HEIGHT * heightScale;

  /**
   * BUILD WIDTH RIDES THE GROUP, NOT THE BONES.
   *
   * The obvious place to put it is a non-uniform scale on hips, spine and
   * chest — and it is wrong twice. Scales COMPOUND down a chain, so a stocky
   * build's 1.3 lands on the chest as 1.3³ = 2.2 and the figure comes back a
   * plank; and a non-uniform scale on a bone that ROTATES is a shear, so the
   * legs would skew through the walk. One non-uniform scale at the top of the
   * tree has neither problem: it is applied once, after the skinning, and it
   * widens the stance along with the shoulders, which is what a broad build
   * looks like from the front.
   *
   * The head is exempt, because a broad person does not have a wide skull.
   * Its bone scale cancels the group's, and carries the jaw slider — the old
   * rig's `headW = 0.88 + jaw*0.24`, which every creator portrait was graded
   * against.
   */
  const b = (BUILDS[a.build] ?? BUILDS[1])?.width ?? 1;
  group.scale.set(heightScale * b, heightScale, heightScale * b);
  const head = skeleton.bones[kit.index.get("head") ?? -1];
  if (head) head.scale.set((0.88 + a.jaw * 0.24) / b, 1, 1 / b);

  const material = characterMaterial(a, lod === 0 ? faceDecal(a) : undefined);
  const meshes: THREE.SkinnedMesh[] = [];
  const identity = new THREE.Matrix4();
  for (const name of piecesFor(a, lod)) {
    const geo = kit.geometries.get(name);
    if (!geo) continue;
    const mesh = new THREE.SkinnedMesh(geo, material);
    mesh.castShadow = true;
    mesh.receiveShadow = lod === 0;
    mesh.frustumCulled = false;
    group.add(mesh);
    mesh.bind(skeleton, identity);
    meshes.push(mesh);
  }

  // Ground contact — the two-lobe pool render/humanoid.ts tuned. A figure
  // that does not darken the floor under it is a cutout standing in front of
  // a room rather than in it, and the night district runs no shadow-casting
  // light at all.
  let pool: THREE.Mesh | undefined;
  let core: THREE.Mesh | undefined;
  if (opts.contact !== false) {
    const stance = Math.max(0.6, 0.66 * b);
    pool = contactShadow(0.5, 1.5, 0.24);
    pool.scale.setScalar(stance * 2.1);
    pool.position.y = 0.01;
    group.add(pool);
    core = contactShadow(0.85, 2.0, 0.46);
    core.scale.set(stance * 1.28, stance * 0.96, 1);
    core.position.set(0, 0.014, 0.02);
    group.add(core);
  }

  const pose = new Pose(skeleton, kit.index);
  let phase = 0;
  let swing = 0;

  function animate(speed: number, dt: number): void {
    const walking = speed > 0.15;
    swing += ((walking ? 1 : 0) - swing) * Math.min(1, dt * 10);
    phase += speed * dt * 3.6;
    pose.apply(phase, swing, performance.now() * 0.001, speed);
    const bob = Math.abs(Math.sin(phase * 2)) * 0.03 * swing;
    group.position.y = bob * heightScale;
    // The ground does not bob with the body.
    if (pool) pool.position.y = 0.01 - bob;
    if (core) core.position.y = 0.014 - bob;
  }
  animate(0, 0);

  function dispose(): void {
    material.dispose();
    for (const mesh of meshes) group.remove(mesh);
    if (pool) (pool.material as THREE.Material).dispose();
    if (core) (core.material as THREE.Material).dispose();
    skeleton.dispose();
  }

  return { group, animate, height, dispose };
}

// ---------------------------------------------------------------------------
// The walk cycle, retargeted.
//
// The curves are render/humanoid.ts's, bone for bone: the thigh swings on a
// sine, the knee hinges on max(0, sin(φ − 0.7)) because a knee only ever bends
// one way and bends hardest just after toe-off, the arms counter-swing, the
// shoulders counter-rotate against the hips, and standing still is a breath.
// What the skeleton adds is what a scene graph of rigid solids could not have:
// the ankle rolls through the step, the spine takes half the shoulder twist so
// the counter-rotation runs through a body rather than snapping at one joint,
// and the head holds its line through all of it — because a walking person
// looks where they are going.

class Pose {
  private q = new THREE.Quaternion();
  private e = new THREE.Euler();
  private rest: THREE.Quaternion[];
  private id: (name: string) => number;

  constructor(
    private skeleton: THREE.Skeleton,
    index: Map<string, number>,
  ) {
    this.rest = skeleton.bones.map((b) => b.quaternion.clone());
    this.id = (name) => index.get(name) ?? -1;
  }

  private turn(name: string, rx: number, ry: number, rz: number): void {
    const i = this.id(name);
    const bone = this.skeleton.bones[i];
    const rest = this.rest[i];
    if (!bone || !rest) return;
    this.q.setFromEuler(this.e.set(rx, ry, rz));
    bone.quaternion.copy(rest).multiply(this.q);
  }

  apply(phase: number, swing: number, idle: number, speed: number): void {
    // A long stride at a run, a shorter one at a walk.
    const legAmp = (0.5 + Math.min(0.18, speed * 0.05)) * swing;
    const armAmp = 0.42 * swing + 0.015;
    for (let i = 0; i < 2; i++) {
      const tag = i === 0 ? "L" : "R";
      const ph = phase + i * Math.PI;
      const knee = Math.max(0, Math.sin(ph - 0.7)) * 0.95 * swing;
      this.turn(`thigh${tag}`, Math.sin(ph) * legAmp, 0, 0);
      this.turn(`shin${tag}`, knee, 0, 0);
      // The ankle rolls: toe down as the leg swings back, dorsiflexed as it
      // comes through. It is four degrees and it is most of what stops a walk
      // reading as two posts sliding along the ground.
      this.turn(`foot${tag}`, -Math.sin(ph - 1.1) * 0.22 * swing - knee * 0.35, 0, 0);
      const sway = Math.sin(idle * 1.7 + i) * 0.02;
      this.turn(`upperArm${tag}`, Math.sin(ph + Math.PI) * armAmp + sway, 0, 0);
      // A swinging arm bends at the elbow on the forward stroke only.
      this.turn(`foreArm${tag}`, Math.max(0, Math.sin(ph + Math.PI)) * 0.3 * swing, 0, 0);
    }
    const twist = -Math.sin(phase) * 0.11 * swing;
    this.turn("spine", 0, twist * 0.45, 0);
    this.turn("chest", Math.sin(idle * 1.1) * 0.012 * (1 - swing), twist * 0.55, 0);
    this.turn("neck", 0, -twist * 0.5, 0);
    this.turn("head", 0, -twist * 0.4, 0);
  }
}

// ---------------------------------------------------------------------------
// THE CROWD.
//
// Ninety-six pedestrians cannot each be a SkinnedMesh: that is ninety-six
// skeletons updated on the CPU and three hundred draw calls. The old answer
// was to give up skinning — bake each rigid node of the figure into its own
// geometry and instance those, which is why the crowd's shoulders were holes
// and why it could never be the same figure the player was.
//
// This is the other answer, and it is the one every game with a crowd in it
// uses. The walk cycle is EVALUATED ONCE, offline-ish (at load, in about a
// millisecond), into a texture of bone matrices: one row per frame, four
// texels per bone. Every instance carries a single float saying which row it
// is on. The vertex shader is three's own skinning path with `getBoneMatrix`
// replaced — so the deformation is the real one, the mesh is the real one, and
// a look costs one draw call for however many people are wearing it.
//
// What is given up: the pose is quantised to 1/32 of a stride, about 20 ms at
// walking pace. At the six metres a pedestrian ever gets to the lens that is
// two frames of hold on a limb moving at a few centimetres a frame, and it
// buys a crowd made of the character the player is wearing.

const FRAMES = 32;

export interface CrowdLook {
  appearance: Appearance;
  count: number;
}

/** Silhouette width for a build, for callers that own their own transform. */
export const buildWidth = (a: Appearance): number => (BUILDS[a.build] ?? BUILDS[1])?.width ?? 1;

/** Sole-to-crown height for an appearance, without building one. */
export const characterHeight = (a: Appearance): number =>
  PLAYER_HEIGHT * (HEIGHT_MIN + (HEIGHT_MAX - HEIGHT_MIN) * a.height);

export interface CrowdBatch {
  group: THREE.Group;
  /** Place one walker: `phase` is its position in the stride, in radians. */
  set(look: number, slot: number, matrix: THREE.Matrix4, phase: number, tint: THREE.Color): void;
  commit(): void;
  triangles: number;
  dispose(): void;
}

/** Bake one look's whole stride into a bone-matrix texture. */
function bakeCycle(a: Appearance): THREE.DataTexture {
  if (!kit) throw new Error("character not loaded");
  const { root, skeleton } = cloneSkeleton();
  // Build width rides the INSTANCE MATRIX for the crowd, exactly as it rides
  // the group for a player — see createCharacter. The cycle is baked from an
  // unscaled skeleton, so all a look changes here is the jaw.
  const b = (BUILDS[a.build] ?? BUILDS[1])?.width ?? 1;
  const head = skeleton.bones[kit.index.get("head") ?? -1];
  if (head) head.scale.set((0.88 + a.jaw * 0.24) / b, 1, 1 / b);

  const pose = new Pose(skeleton, kit.index);
  const n = skeleton.bones.length;
  const data = new Float32Array(n * 4 * 4 * FRAMES);
  const m = new THREE.Matrix4();
  for (let f = 0; f < FRAMES; f++) {
    pose.apply((f / FRAMES) * Math.PI * 2, 1, f * 0.03, 1.25);
    root.updateMatrixWorld(true);
    for (let i = 0; i < n; i++) {
      const bone = skeleton.bones[i];
      const inv = skeleton.boneInverses[i];
      if (!bone || !inv) continue;
      m.multiplyMatrices(bone.matrixWorld, inv);
      data.set(m.elements, (f * n + i) * 16);
    }
  }
  skeleton.dispose();
  const tex = new THREE.DataTexture(data, n * 4, FRAMES, THREE.RGBAFormat, THREE.FloatType);
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

export function crowdBatch(looks: readonly CrowdLook[], lod: 0 | 1 | 2 = 2): CrowdBatch {
  if (!kit) throw new Error("character not loaded");
  const group = new THREE.Group();
  const rows: { meshes: THREE.InstancedMesh[]; frames: THREE.InstancedBufferAttribute[] }[] = [];
  const owned: THREE.InstancedMesh[] = [];
  const textures: THREE.Texture[] = [];
  const materials: THREE.Material[] = [];
  let triangles = 0;

  looks.forEach((look) => {
    const boneTexture = bakeCycle(look.appearance);
    textures.push(boneTexture);
    // The crowd gets a face. It is one canvas per LOOK, not per pedestrian,
    // and a figure six metres away with no eyes in it is the single loudest
    // thing about a crowd — the old one had none and every judge said so.
    const material = characterMaterial(look.appearance, faceDecal(look.appearance));
    material.userData.uniforms.boneTexture = { value: boneTexture };
    material.defines = { ...material.defines, USE_SKINNING: "" };
    material.userData.instanced = true;
    materials.push(material);

    const meshes: THREE.InstancedMesh[] = [];
    const frames: THREE.InstancedBufferAttribute[] = [];
    for (const name of piecesFor(look.appearance, lod)) {
      const src = kit?.geometries.get(name);
      if (!src) continue;
      // An InstancedBufferGeometry that SHARES the source's attribute buffers:
      // four looks must not mean four copies of the same vertices.
      const geo = new THREE.InstancedBufferGeometry();
      geo.index = src.index;
      for (const [key, attr] of Object.entries(src.attributes)) geo.setAttribute(key, attr);
      geo.boundingSphere = src.boundingSphere;
      const frame = new THREE.InstancedBufferAttribute(new Float32Array(look.count), 1);
      frame.setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute("aFrame", frame);
      geo.instanceCount = look.count;
      const mesh = new THREE.InstancedMesh(geo, material, look.count);
      mesh.frustumCulled = false;
      mesh.castShadow = true;
      meshes.push(mesh);
      frames.push(frame);
      group.add(mesh);
      owned.push(mesh);
      triangles += ((src.index?.count ?? 0) / 3) * look.count;
    }
    rows.push({ meshes, frames });
  });

  const TAU = Math.PI * 2;
  return {
    group,
    triangles,
    set(look, slot, matrix, phase, tint) {
      const row = rows[look];
      if (!row) return;
      const f = Math.floor(((((phase / TAU) % 1) + 1) % 1) * FRAMES) % FRAMES;
      row.meshes.forEach((mesh, i) => {
        mesh.setMatrixAt(slot, matrix);
        mesh.setColorAt(slot, tint);
        const frame = row.frames[i];
        if (frame) frame.setX(slot, f);
      });
    },
    commit() {
      for (const row of rows) {
        for (const mesh of row.meshes) {
          mesh.instanceMatrix.needsUpdate = true;
          if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        }
        for (const frame of row.frames) frame.needsUpdate = true;
      }
    },
    dispose() {
      for (const mesh of owned) {
        group.remove(mesh);
        mesh.dispose();
        // Only aFrame belongs to this crowd geometry. The remaining buffers
        // belong to the loaded character kit and can still serve other rooms.
        for (const attribute of Object.keys(mesh.geometry.attributes)) {
          if (attribute !== "aFrame") mesh.geometry.deleteAttribute(attribute);
        }
        mesh.geometry.setIndex(null);
        mesh.geometry.dispose();
      }
      for (const tex of textures) tex.dispose();
      for (const mat of materials) mat.dispose();
    },
  };
}
